import {
  Avatar,
  Button,
  Card,
  Remove,
  Edit,
  ImageBouquet,
  PageHeader,
  theme,
  Check,
  Warning,
} from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { noticeboardConstants } from 'locale/noticeboard'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { Spinner } from 'components/generic'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import dayjs from 'dayjs'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import useNotices, {
  NOTICES_FETCH_TYPE,
  UseNoticesReturnOne,
} from '../hooks/useNotices'
import { useState } from 'react'
import TrashcanIcon from '@lmc/ui-kit/src/atoms/Icon/customShapes/Trashcan'
import { generalConstants } from 'locale/general'
import { UserDtos } from '@lmc/ts-common/lib/dtos/User'
import { Form } from 'antd'
import NoticeForm from './NoticeForm'
import { showToast } from 'common/utils'
import { UserRole } from '@lmc/ts-common'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import { useRoleAccess } from 'components/AccessControl'

const noticeboardUrl = '/noticeboard'

type Person = Pick<UserDtos, 'picture' | 'name' | 'id'>

const ViewNotice = () => {
  const { t } = useTranslation(NAMESPACE.NOTICEBOARD)
  const { t: generalTranslations } = useTranslation(NAMESPACE.GENERAL)
  const { id: noticeID } = useParams()
  const [isReadOnly, setIsReadOnly] = useState(true)
  const [isDirty, setIsDirty] = useState(false)
  const onDeleteSuccess = () => {
    navigate(noticeboardUrl)
    showToast({ title: t(noticeboardConstants.TOAST_DELETE) })
  }
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const {
    result: notice,
    isNoticeLoading,
    deleteNotice,
    updateNotice,
  } = useNotices({
    noticeID,
    fetch: NOTICES_FETCH_TYPE.SINGLE,
    onDeleteSuccess,
  }) as UseNoticesReturnOne

  const handleDelete = () => {
    setIsDirty(false)
    deleteNotice()
  }

  const hasRolePermission = useRoleAccess(
    [
      UserRole.careHomeAdmin,
      UserRole.lmcAdmin,
      UserRole.seniorCarer,
      UserRole.groupAdmin,
    ],
    [
      {
        i: 'update',
        this: 'Notice',
      },
    ],
  )

  if (isNoticeLoading) {
    return <Spinner />
  }

  const sortedSeenBy = notice?.seenBy.sort((a: Person, b: Person) =>
    a.name.localeCompare(b.name),
  )

  const sortedNotSeenBy = notice?.notSeenBy.sort((a: Person, b: Person) =>
    a.name.localeCompare(b.name),
  )

  return (
    <Wrapper>
      <NavigationModal canNavigate={!isDirty} />
      <PageHeader
        title={isReadOnly ? notice?.title : t(noticeboardConstants.EDIT)}
        backButtonHref={noticeboardUrl}
      >
        {!isReadOnly && hasRolePermission && (
          <ActionsWrapper>
            <ConfirmationModal shouldPrompt={isDirty}>
              <CancelButton
                variant="default"
                icon={<Remove />}
                onClick={() => setIsReadOnly(true)}
              >
                {generalTranslations(generalConstants.CANCEL)}
              </CancelButton>
            </ConfirmationModal>
            <ConfirmationModal
              localisation={{
                confirmText: generalTranslations(generalConstants.DELETE),
                cancelText: generalTranslations(generalConstants.CANCEL),
              }}
              modalContent={
                <ConfirmationModalContent>
                  <ConfirmationModalTitleContainer>
                    <Warning color={theme.color.status.warning} size={24} />
                    <Heading level={3}>
                      {t(noticeboardConstants.DELETE_CONFIRMATION_TITLE)}
                    </Heading>
                  </ConfirmationModalTitleContainer>
                  <Divider />
                  <Text>
                    {t(noticeboardConstants.DELETE_CONFIRMATION_TEXT)}
                  </Text>
                  <Divider />
                </ConfirmationModalContent>
              }
            >
              <StyledButton
                variant="danger"
                icon={TrashcanIcon}
                onClick={handleDelete}
              >
                {generalTranslations(generalConstants.DELETE)}
              </StyledButton>
            </ConfirmationModal>
            <StyledButton
              variant="primary"
              icon={<Check color={theme.color.neutral.color11} />}
              onClick={form.submit}
            >
              {generalTranslations(generalConstants.SAVE)}
            </StyledButton>
          </ActionsWrapper>
        )}
        {isReadOnly && (
          <ActionsWrapper>
            <StyledButton
              variant="primary"
              icon={<Edit />}
              onClick={() => setIsReadOnly(false)}
              disabledByPermission={!hasRolePermission}
            >
              {t(noticeboardConstants.EDIT)}
            </StyledButton>
          </ActionsWrapper>
        )}
      </PageHeader>
      {isReadOnly ? (
        <ContentWrapper>
          <InfoCard>
            <InfoBox>
              <InfoText level={2}>
                {t(noticeboardConstants.LAST_UPDATED_ON)}
              </InfoText>
              <ContentContainer>
                <Text>
                  {dayjs(notice?.updatedOn).format('DD-MM-YYYY HH:mm')}
                </Text>
              </ContentContainer>
            </InfoBox>
            <InfoBox>
              <InfoText level={2}>
                {t(noticeboardConstants.LAST_UPDATED_BY)}
              </InfoText>
              <ContentContainer>
                <Avatar size={36} url={notice?.updatedBy?.picture} />
                <Text>{notice?.updatedBy?.name}</Text>
              </ContentContainer>
            </InfoBox>
            <InfoBox>
              <InfoText level={2}> {t(noticeboardConstants.READ_BY)}</InfoText>
              <ContentContainer>
                <ImageBouquet
                  images={
                    sortedSeenBy?.map((person: Person) => person.picture) || []
                  }
                  maximumRenderedItems={3}
                  popoverContent={
                    <PopoverContent>
                      <MembersPopoverText level={2}>
                        {t(noticeboardConstants.TEAM_MEMBERS)}
                      </MembersPopoverText>
                      <MembersPopoverOption>
                        {sortedSeenBy?.map((person: Person) => (
                          <Text key={person.id}>{person.name}</Text>
                        ))}
                      </MembersPopoverOption>
                    </PopoverContent>
                  }
                />
              </ContentContainer>
            </InfoBox>
            <InfoBox>
              <InfoText level={2}>
                {t(noticeboardConstants.NOT_READ_BY)}
              </InfoText>
              <ContentContainer>
                <ImageBouquet
                  images={
                    sortedNotSeenBy?.map(
                      (person: Pick<UserDtos, 'picture' | 'name' | 'id'>) =>
                        person.picture,
                    ) || []
                  }
                  maximumRenderedItems={3}
                  popoverContent={
                    <PopoverContent>
                      <MembersPopoverText level={2}>
                        {t(noticeboardConstants.TEAM_MEMBERS)}
                      </MembersPopoverText>
                      <MembersPopoverOption>
                        {sortedNotSeenBy?.map(
                          (
                            person: Pick<UserDtos, 'picture' | 'name' | 'id'>,
                          ) => <Text key={person.id}>{person.name}</Text>,
                        )}
                      </MembersPopoverOption>
                    </PopoverContent>
                  }
                />
              </ContentContainer>
            </InfoBox>
          </InfoCard>
          <NoticeCard>
            <NoticeCardTitle level={3}>
              {t(noticeboardConstants.MESSAGE)}
            </NoticeCardTitle>
            <StyledDivider />
            {/* <RichEditor html={notice?.body} readOnly={isReadOnly} /> */}
            <ViewRichText
              dangerouslySetInnerHTML={{ __html: notice?.body || '' }}
            />
            {/* {notice?.body} */}
          </NoticeCard>
        </ContentWrapper>
      ) : (
        <ContentWrapper>
          <NoticeForm
            onFinish={(values) => {
              updateNotice(values)
              setIsReadOnly(true)
              showToast({ title: t(noticeboardConstants.TOAST_UPDATE) })
              setIsDirty(false)
            }}
            form={form}
            notice={notice}
            setIsDirty={setIsDirty}
          />
        </ContentWrapper>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  max-width: 1080px;
  padding-top: 24px;
  padding-bottom: 50px;
  margin-left: auto;
  margin-right: auto;
  color: ${(props) => props.theme.color.neutral.color3};

  ${({ theme }) => theme.mediaQueries.tabletDown} {
    padding: 24px;
  }
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 24px;
`

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
`

const ContentContainer = styled.div`
  height: 36px;
  display: flex;
  align-items: center;
  gap: 8px;
`

const ConfirmationModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  color: ${(props) => props.theme.color.neutral.color3};
`

const ConfirmationModalTitleContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`

const InfoCard = styled(Card)`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 40px;

  ${({ theme }) => theme.mediaQueries.tabletDown} {
    flex-wrap: wrap;
    gap: 12px;
  }
`

const InfoBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  ${({ theme }) => theme.mediaQueries.tabletDown} {
    flex: 40%;
  }
`

const InfoText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const NoticeCard = styled(Card)`
  padding: 24px;
`

const PopoverContent = styled.div`
  max-height: 400px;
  overflow: scroll;
`

const MembersPopoverText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const NoticeCardTitle = styled(Heading)``

const StyledDivider = styled(Divider)`
  margin: 16px 0;
`

const StyledButton = styled(Button)`
  white-space: nowrap;
  margin-left: 8px;
`

const ViewRichText = styled.div`
  padding: 12px 0;
  h1 {
    font-size: 32px;
  }
  h2 {
    font-size: 28px;
  }
  h3 {
    font-size: 22px;
  }
`

const CancelButton = styled(Button)`
  margin-left: 8px;
  &:hover {
    svg path {
      stroke: ${({ theme }) => theme.color.status.primary};
    }
  }
`

const MembersPopoverOption = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px;
  border-radius: 9px;
`

export default ViewNotice
